import {createOption, getOption, OptionValue} from "./form";
import {
    GetEventPartyListUsingGETApiReaderTypesEnum,
    GetEventPartyListUsingGETPartyTypesEnum,
    GetEventPartyListUsingGETSexTypeEnum,
    GetPartyListUsingGETInviteTypesEnum,
    GetPartyListUsingGETPartyTypesEnum,
    JsonArticleInfoSexEnum,
    JsonEventParty,
    JsonEventPartyInfo,
    JsonEventPartyInfoAccredStatusEnum,
    JsonEventPartyInfoContractStatusEnum,
    JsonEventPartyInfoSiwiAccredStatusEnum,
    JsonEventPartyMassActionRequest,
    JsonEventPartyStatusEnum,
    JsonFindPartyResponse,
    JsonFindPartyResponseMatchTypesEnum,
    JsonParty,
    JsonPartyInfo,
    JsonPartyInfoPartyTypeEnum,
    JsonPartySiwiStatusEnum,
    JsonPartySiwiSyncStatusEnum,
    JsonPartyStatusEnum
} from "../generated-api";
import {datetimeToGui, dateToGui} from "../helpers/date";
import {
    AccessTimeRounded,
    AccountBalanceRounded,
    AttachMoneyRounded,
    BadgeOutlined,
    CheckroomRounded,
    CommuteRounded,
    DeleteForeverRounded,
    Done,
    DoNotDisturbOutlined,
    ErrorOutline,
    GradingRounded,
    HotelOutlined,
    HourglassEmptyRounded,
    LanguageOutlined,
    LocalBarOutlined,
    LocalParkingOutlined,
    NoAccountsRounded,
    PhotoCameraOutlined, PictureAsPdfOutlined,
    Print,
    PublishedWithChangesRounded,
    QrCode,
    QuestionMarkRounded,
    RestaurantOutlined,
    SettingsRounded,
    WarningAmberRounded
} from "@mui/icons-material";
import * as React from "react";
import {CSSProperties} from "react";
import {articleSize} from "../helpers/format";

type BasePartyInfo = {
    partyType?: string,
    firstName?: string,
    lastName?: string
    companyName?: string,
    birthDate?: string,
    partyId?: number,
    barcode?: string
}

export const partyName = (item: BasePartyInfo, full?: boolean): string => {
    if (!item) {
        return '';
    }
    if (item.partyType === JsonPartyInfoPartyTypeEnum.T || item.partyType === JsonPartyInfoPartyTypeEnum.Po) {
        if (item.companyName) {
            return (item.partyType === JsonPartyInfoPartyTypeEnum.T ? '* ' : '') + item.companyName;
        }
    }

    let n;
    if (item.lastName) {
        n = item.lastName + (item.firstName ? ' ' + item.firstName : '')
    } else if (item.firstName) {
        n = item.firstName;
    } else if (item.companyName) {
        n = item.companyName;
    }
    if (n) {
        return n + (full && item.birthDate ? ' (' + dateToGui(item.birthDate) + ')' : '');
    }

    return 'ID ' + (item.partyId || item.barcode);
}

export const barcode = (barcode?: string) => {
    if (barcode?.indexOf('@')) {
        return barcode?.split('@')[0] // reused code
    }
    return barcode;
}

export const parkPlates = (pp?: string) => {
    return pp?.split(',').map(v => v?.replaceAll(' ', '').trim()).filter(v => !!v).join(', ');
}

export const normalizeName = (a?: string) => {
    return a?.normalize('NFD').replace(/\p{Diacritic}/gu, "") || '';
}

export const isNameEqual = (a?: string, b?: string): boolean => {
    if (!a || !b) {
        return false;
    }
    if (a === b) {
        return true;
    }
    const al = a.toLowerCase();
    const bl = b.toLowerCase();
    if (al === bl) {
        return true;
    }

    const an = normalizeName(al);
    if (an === bl) {
        return true;
    }
    const bn = normalizeName(bl);
    if (an === bn) {
        return true;
    }
    return an.replace(/sen\.|st\.|nejst\.|ml\.|nejml\.|jun\./, '').replace(/\.,/, '').trim()
        === bn.replace(/sen\.|st\.|nejst\.|ml\.|nejml\.|jun\./, '').replace(/\.,/, '').trim();
}

export const isStringEqualLenient = (a?: string, b?: string): boolean => {
    if (a === b) {
        return true;
    }
    return a?.trim() === b?.trim();
}

export const doesNameContains = (search: string, name?: string): boolean => {
    if (!search || !name) {
        return false;
    }
    if (search === name || name.indexOf(search) >= 0) {
        return true;
    }
    const s = search.toLowerCase();
    const n = name.toLowerCase();
    if (s === n || n.indexOf(s) >= 0) {
        return true;
    }

    const sn = normalizeName(s);
    if (sn === n) {
        return true;
    }
    const nn = normalizeName(n);
    if (sn === nn) {
        return true;
    }
    return nn.indexOf(sn) >= 0;
}

export function levenshtein(a: string, b: string): number {
    const an = a ? a.length : 0;
    const bn = b ? b.length : 0;
    if (an === 0) {
        return bn;
    }
    if (bn === 0) {
        return an;
    }
    const matrix = new Array<number[]>(bn + 1);
    for (let i = 0; i <= bn; ++i) {
        let row = matrix[i] = new Array<number>(an + 1);
        row[0] = i;
    }
    const firstRow = matrix[0];
    for (let j = 1; j <= an; ++j) {
        firstRow[j] = j;
    }
    for (let i = 1; i <= bn; ++i) {
        for (let j = 1; j <= an; ++j) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1], // substitution
                    matrix[i][j - 1], // insertion
                    matrix[i - 1][j] // deletion
                ) + 1;
            }
        }
    }
    return matrix[bn][an];
}

export const foundPartyScore = (f: JsonFindPartyResponse) => {
    let as = 0;
    if (!f.matchTypes) {
        return as;
    }
    if (f.matchTypes.indexOf(JsonFindPartyResponseMatchTypesEnum.Name) >= 0) {
        as += 0.1;
    }
    if (f.photoScore !== undefined && f.matchTypes.indexOf(JsonFindPartyResponseMatchTypesEnum.Photo) >= 0) {
        as += 0.9 - f.photoScore;
    }
    return as;
}

export const sortFoundParties = (items?: JsonFindPartyResponse[]): JsonFindPartyResponse[] => {
    if (!items) {
        return [];
    }
    const queryIds: string[] = [];
    items.forEach((f) => {
        if (f.queryId && queryIds.indexOf(f.queryId) < 0) {
            queryIds.push(f.queryId);
        }
    });

    const res: JsonFindPartyResponse[] = [];
    queryIds.forEach((queryId) => {
        const sorted = items
            .filter((f) => f.queryId === queryId)
            .sort((a, b) => {
                const as = foundPartyScore(a);
                const bs = foundPartyScore(b);
                return as === bs ? 0 : (as > bs ? -1 : 1);
            });
        let count = 0, maxScore = 0, limit = 4;
        sorted.forEach((f) => {
            const s = foundPartyScore(f);
            const isByName = f.matchTypes?.indexOf(JsonFindPartyResponseMatchTypesEnum.Name) !== undefined
                && f.matchTypes.indexOf(JsonFindPartyResponseMatchTypesEnum.Name) >= 0;
            if (maxScore > 0.8 && (maxScore - s) > 0.1 && !isByName) {
                return;
            }
            if (isByName) {
                limit++;
            }

            if (count >= limit) {
                return;
            }
            res.push(f);
            maxScore = s;
            count++;
        });
    });

    return res;
}

/** An article type as the invite and the issuing rules see it. */
export interface ArticleTypeFamily {
    articleTypeId?: number;
    title?: string;
    isOffered?: boolean;
    reuseTypeIds?: number[];
}

/** The invite asks about the thing, not about its season, so the year is left out of its name. */
export const articleTypeTitle = (title?: string): string => (title || '').replace(/\s+(19|20)\d{2}$/, '');

/** What a party already has of that kind, from an earlier season. */
export interface ArticleReuse {
    articleTypeId?: number;
    reuseArticleId?: number;
    reuseYear?: number;
    reuseSex?: string;
    reuseSize?: string;
}

const articleTypeYear = (title?: string): number => parseInt((title || '').match(/(\d{4})/)?.[1] || '0', 10);

/** This season's types first, the leftovers of the older ones behind them, newest first. */
export const compareArticleTypes = (a: ArticleTypeFamily, b: ArticleTypeFamily): number => {
    if ((a.isOffered === false) !== (b.isOffered === false)) {
        return a.isOffered === false ? 1 : -1;
    }
    if (a.isOffered === false) {
        return (articleTypeYear(b.title) - articleTypeYear(a.title)) || (a.title || '').localeCompare(b.title || '');
    }
    return 0;
};

/**
 * The piece of that kind the party already has, or nothing when it is a leftover shown in its own
 * column - there is no point in reporting the same piece twice.
 */
export const articleReuse = <T extends ArticleTypeFamily>(at: T, details?: ArticleReuse[], types?: T[]): ArticleReuse | undefined => {
    const reuse = details?.find((d) => d.articleTypeId === at.articleTypeId);
    if (!reuse?.reuseArticleId) {
        return undefined;
    }
    const isShownAsLeftover = at.isOffered !== false && (types || []).some((o) => o.isOffered === false
        && details?.find((d) => d.articleTypeId === o.articleTypeId)?.reuseArticleId === reuse.reuseArticleId);
    return isShownAsLeftover ? undefined : reuse;
};

/**
 * What to show when nothing was issued yet: the size of the very same article a party got in an earlier
 * season, or just the season of a similar one (that is what a leftover column says, hence the size there).
 */
export const articleReuseLabel = (at: ArticleTypeFamily, reuse?: ArticleReuse): string | undefined => {
    if (!reuse) {
        return undefined;
    }
    return at.isOffered === false && reuse.reuseSize
        ? articleSize(reuse.reuseSex, reuse.reuseSize, true)
        : (reuse.reuseYear ? '' + reuse.reuseYear : undefined);
};

/** Column header of a grid: initials and the season, so that Bunda 2027 and Bunda 2024 do not collide. */
export const articleTypeShortTitle = (title?: string): string => {
    const year = (title || '').match(/(19|20)(\d{2})/);
    return articleTypeTitle(title).replaceAll(/[()]/g, '').trim().split(' ')
        .map((w) => w[0]?.toUpperCase() || '').join('') + (year ? year[2] : '');
};

/** Article type together with its position in the full list - replyData.articles is indexed by it. */
export interface OfferedArticleType<T> {
    at: T;
    index: number;
}

/**
 * Types the invite asks a preference for. Anything else in the event is a leftover of an older season,
 * issued from the stock but never asked about. Events before 2027 have the flag set by the migration.
 */
export const offeredArticleTypes = <T extends ArticleTypeFamily>(
    articleTypes?: T[]
): OfferedArticleType<T>[] => {
    if (!articleTypes?.length) {
        return [];
    }
    const offered: OfferedArticleType<T>[] = [];
    articleTypes.forEach((at, index) => {
        if (at.isOffered !== false) {
            offered.push({at, index});
        }
    });
    return offered;
};

export const isArticleTypeOffered = <T extends ArticleTypeFamily>(
    articleType: T, articleTypes?: T[]
): boolean => offeredArticleTypes(articleTypes).some((o) => o.at.articleTypeId === articleType.articleTypeId);

export const articleName = (article?: { sex?: string, size?: string }, short?: boolean, partySex?: string) => {
    if (!article) {
        return null;
    }
    const o = articleSexOptions.find((o) => o.value === article.sex);
    const sex = o?.label || article.sex || '';
    const isDiff = ((partySex === 'M' && article.sex === 'F') || (partySex === 'F' && article.sex === 'M'));

    const sizeLabel = articleSize(article.sex, article.size, short);
    const sexLabel = short
        ? (partySex && !isDiff ? '' : ' (' + sex[0] + ') ')
        : o?.tooltip ? ' ' + o.tooltip : '';

    return <span title={article.size + ' ' + (o?.tooltip || ('(' + article.sex + ')'))} style={isDiff ? {color: 'var(--color-warning)'} : undefined}>
            {sizeLabel}{sexLabel}
        </span>;
}

export const renderAccredStatus = (_: any, item: JsonEventPartyInfo) => {
    const style: CSSProperties = {};
    let icon, title;
    if (item.siwiAccredStatus) {
        const o = getOption(item.siwiAccredStatus, siwiAccredStatusOptions);
        icon = o.icon || item.siwiAccredStatus[0];
        title = (o.tooltip || o.label) + (item.accredAt ? ' (' + datetimeToGui(item.accredAt) + ')' : '');
    }
    if (item.accredStatus === JsonEventPartyInfoAccredStatusEnum.EmsApproved) {
        style.color = 'var(--color-info)';
        if (!icon) {
            const o = getOption(JsonEventPartyInfoSiwiAccredStatusEnum.EmsPending, siwiAccredStatusOptions);
            icon = o.icon;
            title = (o.tooltip || o.label)
        }
    }
    if (!icon) {
        return null;
    }

    return <span title={title} style={style}>{icon}</span>
}

export const mergeEventDays = (eventDays?: string, mergeEventDays?: string) => {
    let merged = '';
    for (let i = 0; i < Math.max(eventDays?.length || 0, mergeEventDays?.length || 0); i++) {
        if (eventDays?.[i] === '1' || mergeEventDays?.[i] === '1') {
            merged += '1';
        } else {
            merged += '0';
        }
    }
    return merged;
}

export interface PartyMassActionValues<F> {
    filter: F,
    parties: JsonPartyInfo[],
    eventParties: JsonEventPartyInfo[],
    values: JsonEventPartyMassActionRequest
}

export interface PartyMassActionProps<MA extends PartyMassActionValues<F>, F> {
    massAction: MA,
    onSave?: (values: MA) => any;
    onCancel?: () => void;
}

export interface EventPartyImportResult {
    parties: JsonParty[],
    eventParties: JsonEventParty[]
}

export const partyTypeOptions: OptionValue[] = [
    createOption(GetPartyListUsingGETPartyTypesEnum.Fo, 'FO', 'Fyzická osoba'),
    // createOption(GetPartyListUsingGETPartyTypesEnum.Po, 'PO', 'Právnická osoba'),
    createOption(GetPartyListUsingGETPartyTypesEnum.T, 'T', 'Technická osoba')
]

export const partyStatusOptions: OptionValue[] = [
    createOption(JsonPartyStatusEnum.Deleted, 'Neaktivní'),
    createOption(JsonPartyStatusEnum.Active, 'Aktivní'),
]

export const partySiwiStatusOptions: OptionValue[] = [
    createOption(JsonPartySiwiStatusEnum.Deleted, 'Neaktivní'),
    createOption(JsonPartySiwiStatusEnum.Active, 'Aktivní'),
]

export const eventPartyTypeOptions: OptionValue[] = [
    createOption(GetEventPartyListUsingGETPartyTypesEnum.Fo, 'FO', 'Fyzická osoba'),
    // createOption(GetEventPartyListUsingGETPartyTypesEnum.Po, 'PO', 'Právnická osoba'),
    createOption(GetEventPartyListUsingGETPartyTypesEnum.T, 'T', 'Technická osoba')
]

export const eventPartySexTypeOrTechnicalOptions: OptionValue[] = [
    createOption(GetEventPartyListUsingGETSexTypeEnum.M, 'M', 'Muž'),
    createOption(GetEventPartyListUsingGETSexTypeEnum.F, 'Ž', 'Žena'),
    createOption(GetEventPartyListUsingGETPartyTypesEnum.T, 'T', 'Technická osoba')
]

export const eventPartyContractStatusOptions: OptionValue[] = [
    createOption(JsonEventPartyInfoContractStatusEnum.None, 'Nepodepsána', 'Smlouva nepodepsána', <NoAccountsRounded/>),
    createOption(JsonEventPartyInfoContractStatusEnum.Signed, 'Podepsána', 'Smlouva podepsána', <GradingRounded/>),
    createOption(JsonEventPartyInfoContractStatusEnum.Scanned, 'Naskenována', 'Smlouva naskenována', <PictureAsPdfOutlined/>),
]

export const eventPartyContractStatusShortOptions: OptionValue[] = [
    createOption(JsonEventPartyInfoContractStatusEnum.Signed, '', 'Smlouva podepsána', <GradingRounded/>),
    createOption(JsonEventPartyInfoContractStatusEnum.Scanned, '', 'Smlouva naskenována', <PictureAsPdfOutlined/>),
    createOption(JsonEventPartyInfoContractStatusEnum.None, 'Ne', 'Smlouva nepodepsána'),
]

export const eventPartyStatusOptions: OptionValue[] = [
    createOption(JsonEventPartyStatusEnum.Pending, 'Nepotvrzená', 'Čeká se na potvrzení účasti',
        <HourglassEmptyRounded/>),
    createOption(JsonEventPartyStatusEnum.Active, 'Aktivní', 'Registrována v události', <Done/>),
    createOption(JsonEventPartyStatusEnum.Deleted, 'Odstraněná', 'Osoba byla z události odstraněna',
        <DeleteForeverRounded/>),
]

export const sexOptions: OptionValue[] = [
    createOption(GetEventPartyListUsingGETSexTypeEnum.M, 'M', 'Muž'),
    createOption(GetEventPartyListUsingGETSexTypeEnum.F, 'Ž', 'Žena'),
]

export const articleSexOptions: OptionValue[] = [
    createOption(JsonArticleInfoSexEnum.M, 'M', '(muži)'),
    createOption(JsonArticleInfoSexEnum.F, 'Ž', '(ženy)'),
    createOption(JsonArticleInfoSexEnum.U, 'U', undefined),
]

export const isSiwiOptions: OptionValue[] = [
    createOption(true, 'SW', 'Je zaveden v SIWIDATA a má SIWI ID'),
    createOption(false, 'Ne', 'Nemá SIWI ID'),
]

export const isCebOptions: OptionValue[] = [
    createOption(true, 'ČSB', 'Má evidované ČSB ID'),
    createOption(false, 'Nemá', 'Nemá ČSB ID'),
]

export const isUserOptions: OptionValue[] = [
    createOption(true, 'Uživatel', 'Má založen uživatelský přístup'),
    createOption(false, 'Ne', 'Nemá založen uživatelský přístup'),
]

export const isPhotoOptions: OptionValue[] = [
    createOption(true, '', 'Má fotografii', <PhotoCameraOutlined/>),
    createOption(false, 'Ne', 'Nemá fotografii'),
]

export const partyInviteOptions: OptionValue[] = [
    createOption(GetPartyListUsingGETInviteTypesEnum.Any, 'Pozvaná', 'Osoba byla pozvána na událost, s jakýmkoli výsledkem'),
    createOption(GetPartyListUsingGETInviteTypesEnum.Expired, 'Pozvaná, bez odpovědi', 'Osoba byla pozvána, nikdy neodpověděla'),
    createOption(GetPartyListUsingGETInviteTypesEnum.Rejected, 'Pozvaná, odmítla', 'Osoba byla pozvána, odpověděla odmítnutím'),
    createOption(GetPartyListUsingGETInviteTypesEnum.AcceptedNoShow, 'Pozvaná, nedorazila', 'Osoba byla pozvána, přijala, nedorazila'),
    createOption(GetPartyListUsingGETInviteTypesEnum.AcceptedPartialShow, 'Pozvaná, dorazila částečně', 'Osoba byla pozvána, přijala, dorazila na některé potvrzené dny'),
    createOption(GetPartyListUsingGETInviteTypesEnum.AcceptedFullShow, 'Pozvaná, dorazila na vše', 'Osoba byla pozvána, přijala, dorazila na všechny potvrzené dny'),
    createOption(GetPartyListUsingGETInviteTypesEnum.None, 'Nepozvaná', 'Osoba nebyla vůbec pozvána na událost'),
]

export const siwiSyncStatusesOptions: OptionValue[] = [
    // createOption(JsonPartySiwiSyncStatusEnum.None, '(není)', 'xxx'),
    // createOption(JsonPartySiwiSyncStatusEnum.Synced, 'Zapsaná', 'xxx'),
    createOption(JsonPartySiwiSyncStatusEnum.Dirty, 'Čeká', 'Čeká se na propsání změn do Siwi',
        <HourglassEmptyRounded/>),
    createOption(JsonPartySiwiSyncStatusEnum.Conflict, 'Konflikt', 'Je nutné vyřešit možné shody',
        <QuestionMarkRounded/>),
    createOption(JsonPartySiwiSyncStatusEnum.Error, 'Chyba', 'Chyba synchronizace', <WarningAmberRounded/>),
]

export const isMissingInfoOptions: OptionValue[] = [
    createOption(true, '', 'Chybí doplněné některé údaje', <ErrorOutline/>),
    createOption(false, 'Ne', 'Žádné údaje nechybí'),
]

export const isPrefAcmOptions: OptionValue[] = [
    createOption(true, '', 'Má zájem o ubytování', <HotelOutlined/>),
    createOption(false, 'Ne', 'Nemá zájem o ubytování'),
]

export const isPrefParkOptions: OptionValue[] = [
    createOption(true, '', 'Má zájem o parkování', <LocalParkingOutlined/>),
    createOption(false, 'Ne', 'Nemá zájem o parkování'),
]

export const isRectArticleOptions: OptionValue[] = [
    createOption(true, '', 'Artikly vydány', <CheckroomRounded/>),
    createOption(false, 'Ne', 'Žádné artikly nevydány'),
]

export const isContractOptions: OptionValue[] = [
    createOption(true, '', 'Smlouva podepsána', <GradingRounded/>),
    createOption(false, 'Ne', 'Bez podepsané smlouvy'),
]

export const isTravelOptions: OptionValue[] = [
    createOption(true, '', 'Cestovní náhrady', <CommuteRounded/>),
    createOption(false, 'Ne', 'Bez cestovních náhrad'),
]

export const isCompOptions: OptionValue[] = [
    createOption(true, '', 'Odměny', <AttachMoneyRounded/>),
    createOption(false, 'Ne', 'Bez odměn'),
];

export const isPaidOptions: OptionValue[] = [
    createOption(true, '', 'Vyplaceno', <AccountBalanceRounded/>),
    createOption(false, 'Ne', 'Nevyplaceno'),
];

export const apiReaderTypeOptions: OptionValue[] = [
    createOption(GetEventPartyListUsingGETApiReaderTypesEnum.Any, 'Kdekoli', 'Načtena kdekoli', <QrCode/>),
    createOption(GetEventPartyListUsingGETApiReaderTypesEnum.Vip, 'VIP', 'Načtena VIP', <LocalBarOutlined/>),
    createOption(GetEventPartyListUsingGETApiReaderTypesEnum.Press, 'PRESS', 'Načtena PRESS', <LanguageOutlined/>),
    createOption(GetEventPartyListUsingGETApiReaderTypesEnum.Catering, 'Catering', 'Načtena catering', <RestaurantOutlined/>),
    createOption(GetEventPartyListUsingGETApiReaderTypesEnum.None, 'Nenačtena', 'Osoba nenačtena', <DoNotDisturbOutlined/>),
]

export const siwiAccredStatusOptions: OptionValue[] = [
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.None, '(bez akr.)'),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.EmsDraft, 'Koncept', 'Rozpracovaná, zatím neodeslaná do Siwidata',
        <SettingsRounded/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.EmsPending, 'K odeslání', 'Potvrzená, čeká na odeslání do Siwidata',
        <PublishedWithChangesRounded/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.SiwiAny, '(jakákoli Siwi akr.)'),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Entered, 'Vyžádaná', 'Čekáme na potvrzení ze strany Siwidata',
        <HourglassEmptyRounded/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Approved, 'Potvrzená', 'Potvrzená za Siwidata, čekáme na tisk',
        <Done/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Batchprint, 'V dávce', 'Potvrzená za Siwidata, čekáme dávkový tisk',
        <AccessTimeRounded/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Printqueue, 'Tiskne se', 'Probíhá tisk na straně Siwidata',
        <Print/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Printed, 'Vytištěná', 'Kartička fyzicky vytištěná',
        <BadgeOutlined/>),
    createOption(JsonEventPartyInfoSiwiAccredStatusEnum.Deleted, 'Zrušená', 'Zrušeno v Siwidata',
        <DeleteForeverRounded/>),
]

// export const isAccredOptions: OptionValue[] = [
//     createOption(true, 'Akreditace', 'Má zadanou akreditaci v Siwidata'),
//     createOption(false, 'Nemá', 'Nemá zadanou akreditaci v Siwidata'),
// ]
